import React, { useEffect, useState } from 'react';

import {
  BugOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  MoreOutlined,
  TableOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Dropdown,
  MenuProps,
  Modal,
  Row,
  Table,
  TableProps,
  message,
} from 'antd';
import Search from 'antd/es/input/Search';
import Column from 'antd/es/table/Column';
import { SearchProps } from 'antd/lib/input';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import ColumnMappingModal from 'scenes/DataIngester/UploadFilesHistory/Components/ColumnMappingModal';
import ErrorDetailModal from 'scenes/DataIngester/UploadFilesHistory/Components/ErrorDetailModal';
import UploadFileModal from 'scenes/DataIngester/UploadFilesHistory/Components/UploadFileModal';

import { ApiErrorData } from 'models/ApiResponse';
import { DiClient } from 'models/DiClient';
import { DiIngestJob } from 'models/DiIngestJob';
import { DiIngestJobGridMode } from 'models/DiIngestJobGridMode';
import { DiParsingFormat } from 'models/DiParsingFormat';
import { ServerSideDataSchema } from 'models/ServerSideDataSchema';
import { TableParams } from 'models/TableParams';

import { DiClientService } from 'services/DiClientService';
import {
  DiIngestJobService,
  MatchColumnsRowSchema,
} from 'services/DiIngestJobService';
import { DiTableColumn } from 'services/DiTableColumn';

import { FunctionsHelper } from 'utils/FunctionsHelper';

import Layout from '../../../components/Layout/Layout';
import { MAIN_BACKEND_URL } from '../../../config/Config';
import { UPDATE_DATA_INGESTOR_EVENT } from '../../../config/Constants';
import appI18n from '../../../config/i18n';
import { useSocketEvent } from '../../../context/WebSocketContext';

import './UploadFilesHistory.scss';

const PAGE_SIZE = 15;

function UploadFilesHistory() {
  const [messageApi, contextHolder] = message.useMessage();
  const [modal, contextModalHolder] = Modal.useModal();
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [dataSource, setDataSource] = useState<DiIngestJobGridMode[]>([]);
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: PAGE_SIZE,
    },
  });
  const [selectedRow, setSelectedRow] = useState<
    DiIngestJobGridMode | undefined
  >(undefined);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isErrorDetailModalOpen, setIsErrorDetailModalOpen] = useState(false);
  const [isNewResourceModalOpen, setIsNewResourceModalOpen] = useState(false);

  const [diClients, setDiClients] = useState<DiClient[]>([]);
  const [diTableColumns, setDiTableColumns] = useState<DiTableColumn[]>([]);
  const [diParsingFormats, setDiParsingFormats] = useState<DiParsingFormat[]>(
    []
  );
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const dateFormat: string = useSelector(
    (state: any) => state.intl?.dateFormat
  );

  /* Filters */
  const [term, setTerm] = useState('');

  useEffect(() => {
    init();
  }, []);

  useSocketEvent(
    UPDATE_DATA_INGESTOR_EVENT,
    async (data: { id: number; newStatus: string }) => {
      console.log(`Event: ${UPDATE_DATA_INGESTOR_EVENT} - Triggered`, data);

      const checkIndexDataSource = dataSource.findIndex((record) => {
        return record.id === data.id;
      });

      if (checkIndexDataSource !== -1) {
        const jobResponse = await DiIngestJobService.findGridModeById(data.id);

        if (jobResponse.success) {
          const job = jobResponse.data as DiIngestJobGridMode;
          const newDataSource = [...dataSource];
          newDataSource[checkIndexDataSource] = job;
          setDataSource(newDataSource);
        }
      }
    }
  );

  useEffect(() => {
    fetchData();
  }, [
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
    tableParams.sortField,
    tableParams.sortOrder,
    term,
  ]);

  const init = async () => {
    const [clientResponse, tableColumnResponse, parsingFormatResposne] =
      await Promise.all([
        DiClientService.getByCurrentCompany(),
        DiIngestJobService.getAllTableColumnsByCurrentCompany(),
        DiIngestJobService.getAllParsingFormats(),
      ]);

    if (
      clientResponse.success &&
      tableColumnResponse.success &&
      parsingFormatResposne.success
    ) {
      setDiClients(clientResponse.data as DiClient[]);
      setDiTableColumns(tableColumnResponse.data as DiTableColumn[]);
      setDiParsingFormats(parsingFormatResposne.data as DiParsingFormat[]);
      setLoading(false);
    } else {
      if (!clientResponse.success) {
        const error = clientResponse.data as ApiErrorData;
        messageApi.error(
          (error.message as string) ||
            t(
              'scenes.data_ingester.upload_files_history.scene.errors.fetch_grid'
            ),
          3.5
        );
      }

      if (!tableColumnResponse.success) {
        const error = tableColumnResponse.data as ApiErrorData;
        messageApi.error(
          (error.message as string) ||
            t(
              'scenes.data_ingester.upload_files_history.scene.errors.fetch_grid'
            ),
          3.5
        );
      }

      if (!parsingFormatResposne.success) {
        const error = parsingFormatResposne.data as ApiErrorData;
        messageApi.error(
          (error.message as string) ||
            t(
              'scenes.data_ingester.upload_files_history.scene.errors.fetch_grid'
            ),
          3.5
        );
      }
    }
  };

  const onSearch: SearchProps['onSearch'] = (value) => {
    setTerm(value);
  };

  const createResourceModal = () => {
    setIsNewResourceModalOpen(true);
  };

  const showDetailResource = async (row: DiIngestJobGridMode) => {
    setSelectedRow(row);
    setIsDetailModalOpen(true);
  };

  const showErrorDetailResource = async (row: DiIngestJobGridMode) => {
    setSelectedRow(row);
    setIsErrorDetailModalOpen(true);
  };

  const handleCloseNewResourceModal = () => {
    setIsNewResourceModalOpen(false);
  };

  const handleCloseShowDetailResourceModal = () => {
    setSelectedRow(undefined);
    setIsDetailModalOpen(false);
  };

  const handleCloseShowErrorDetailResourceModal = () => {
    setSelectedRow(undefined);
    setIsErrorDetailModalOpen(false);
  };

  const downloadFile = async (row: DiIngestJobGridMode) => {
    try {
      const response = await fetch(
        `${MAIN_BACKEND_URL}/file/get?download=true&key=${row.s3FileKey}`
      );

      if (!response.ok) {
        throw new Error('Error al descargar el archivo');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = row.originalFilename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error descargando el archivo:', error);
    }
  };

  const getDropdownMenu = (row: DiIngestJobGridMode): MenuProps['items'] => {
    const menus: MenuProps['items'] = [];
    let itemNro = 1;

    menus.push({
      key: `${itemNro++}`,
      label: (
        <span>
          <DownloadOutlined style={{ marginRight: '8px' }} />{' '}
          {t(
            'scenes.data_ingester.upload_files_history.scene.dropdown.items.download_file'
          )}
        </span>
      ),
      onClick: () => {
        downloadFile(row);
      },
    });

    if (row.statusCode === 'awaiting_mapping') {
      menus.push({
        key: `${itemNro++}`,
        label: (
          <span>
            <TableOutlined style={{ marginRight: '8px' }} />{' '}
            {t(
              'scenes.data_ingester.upload_files_history.scene.dropdown.items.map_columns'
            )}
          </span>
        ),
        onClick: () => {
          showDetailResource(row);
        },
      });
    }

    if (
      ['initial_extraction_error', 'final_extraction_error'].includes(
        row.statusCode
      )
    ) {
      menus.push({
        key: `${itemNro++}`,
        label: (
          <span>
            <BugOutlined style={{ marginRight: '8px' }} />{' '}
            {t(
              'scenes.data_ingester.upload_files_history.scene.dropdown.items.show_error_detail'
            )}
          </span>
        ),
        onClick: () => {
          showErrorDetailResource(row);
        },
      });
    }

    if (
      ['uploaded', 'awaiting_mapping', 'ready_for_final_extraction'].includes(
        row.statusCode
      )
    ) {
      menus.push({
        key: `${itemNro++}`,
        label: (
          <span>
            <DeleteOutlined style={{ marginRight: '8px' }} />{' '}
            {t(
              'scenes.data_ingester.upload_files_history.scene.dropdown.items.cancel_task'
            )}
          </span>
        ),
        danger: true,
        onClick: () => {
          showCancelConfirmation(row);
        },
      });
    }

    return menus;
  };

  const handleTableChange: TableProps<DiIngestJobGridMode>['onChange'] = (
    pagination,
    filters,
    sorter
  ) => {
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });

    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setDataSource([]);
    }
  };

  const fetchData = async (currentPage?: number) => {
    setTableLoading(true);

    const termFilter = FunctionsHelper.normalizeText(term.trim());

    const response = await DiIngestJobService.getByCurrentCompany({
      page: currentPage || tableParams.pagination?.current || 1,
      pageSize: tableParams.pagination?.pageSize || PAGE_SIZE,
      term: termFilter || undefined,
    });

    if (response.success) {
      const data = response.data as ServerSideDataSchema<DiIngestJobGridMode>;
      setDataSource(data.data);
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: parseInt(`${data.totalRowsFiltered}`),
        },
      });
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t(
            'scenes.data_ingester.upload_files_history.scene.errors.fetch_grid'
          ),
        3.5
      );
    }

    setTableLoading(false);
  };

  const uploadFile = async (
    clientId: number,
    description: string,
    file: File
  ) => {
    const response = await DiIngestJobService.create(
      clientId,
      description.trim(),
      file
    );

    if (response.success) {
      const { createdId } = response.data as { createdId: number };

      await fetchData();

      messageApi.success(
        t(
          'scenes.data_ingester.upload_files_history.scene.messages.upload_success',
          { id: createdId }
        ),
        3.5
      );

      handleCloseNewResourceModal();
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t(
            'scenes.data_ingester.upload_files_history.scene.errors.fetch_grid'
          ),
        3.5
      );
    }
  };

  const saveColumnMapping = async (
    job: DiIngestJob,
    mappings: MatchColumnsRowSchema[]
  ) => {
    const response = await DiIngestJobService.matchColumns(job.id, {
      rows: mappings,
    });

    if (response.success) {
      messageApi.success(
        t(
          'scenes.data_ingester.upload_files_history.scene.messages.save_mapping_success',
          { id: job.id, filename: job.originalFilename }
        ),
        3.5
      );

      await fetchData();

      handleCloseShowDetailResourceModal();
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t(
            'scenes.data_ingester.upload_files_history.scene.errors.fetch_grid'
          ),
        3.5
      );
    }
  };

  const showCancelConfirmation = async (row: DiIngestJobGridMode) => {
    modal.confirm({
      title: t(
        'scenes.data_ingester.upload_files_history.scene.confirmation.title'
      ),
      icon: <ExclamationCircleOutlined />,
      content: (
        <span>
          {t(
            'scenes.data_ingester.upload_files_history.scene.confirmation.content_cancel_task',
            { id: row.id }
          )}
        </span>
      ),
      okText: t(
        'scenes.data_ingester.upload_files_history.scene.confirmation.ok_text'
      ),
      cancelText: t(
        'scenes.data_ingester.upload_files_history.scene.confirmation.cancel_text'
      ),
      onOk: async () => {
        setTableLoading(true);
        const response = await DiIngestJobService.cancel(row.id);

        if (response.success) {
          await fetchData();
          messageApi.success(
            <span>
              {t(
                'scenes.data_ingester.upload_files_history.scene.messages.cancel_task_success',
                { id: row.id }
              )}
            </span>
          );
        } else {
          const error = response.data as ApiErrorData;
          messageApi.error(
            (error.message as string) ||
              t(
                'scenes.data_ingester.upload_files_history.scene.errors.cancel_task_failed'
              ),
            3.5
          );
          setTableLoading(false);
        }
      },
    });
  };

  return (
    <>
      {contextHolder}
      {contextModalHolder}

      <Layout loading={loading} title={''}>
        <Card>
          <div className="filter-container">
            <Row gutter={[24, 16]}>
              <Col xs={24} md={24} lg={12} xl={10} xxl={8}>
                <label className="filter-search-container">
                  {t(
                    'scenes.data_ingester.upload_files_history.scene.labels.filter_by'
                  )}{' '}
                  <Search
                    placeholder={t(
                      'scenes.data_ingester.upload_files_history.scene.labels.file_name'
                    )}
                    onSearch={onSearch}
                    disabled={tableLoading}
                    allowClear
                  />
                </label>
              </Col>
              <Col
                xs={24}
                md={24}
                lg={12}
                xl={14}
                xxl={16}
                className="filter-buttons-container"
              >
                <Button
                  type="primary"
                  onClick={createResourceModal}
                  disabled={tableLoading}
                >
                  <UploadOutlined />{' '}
                  {t(
                    'scenes.data_ingester.upload_files_history.scene.buttons.upload_file'
                  )}
                </Button>
              </Col>
            </Row>
          </div>
        </Card>

        <Card style={{ marginTop: '12px' }}>
          <Table<DiIngestJobGridMode>
            dataSource={dataSource}
            bordered
            loading={tableLoading}
            size="small"
            scroll={{ x: 1300 }}
            pagination={tableParams.pagination}
            rowKey={(record) => record.id}
            onChange={handleTableChange}
          >
            <Column
              title={t(
                'scenes.data_ingester.upload_files_history.scene.table.columns.id'
              )}
              dataIndex="id"
              key="id"
              width={60}
            />

            <Column
              title={t(
                'scenes.data_ingester.upload_files_history.scene.table.columns.carrier'
              )}
              key="clientName"
              dataIndex="clientName"
            />

            <Column
              title={t(
                'scenes.data_ingester.upload_files_history.scene.table.columns.original_filename'
              )}
              key="originalFilename"
              dataIndex="originalFilename"
            />

            <Column
              title={t(
                'scenes.data_ingester.upload_files_history.scene.table.columns.owner'
              )}
              key="ownerName"
              dataIndex="ownerName"
            />

            <Column
              title={t(
                'scenes.data_ingester.upload_files_history.scene.table.columns.description'
              )}
              key="description"
              dataIndex="description"
              align="left"
            />

            <Column
              width={120}
              align="center"
              title={t(
                'scenes.data_ingester.upload_files_history.scene.table.columns.created_at'
              )}
              dataIndex="createdAt"
              key="createdAt"
              render={(createdAt: Date) => (
                <span>{dayjs(createdAt).format(dateFormat)}</span>
              )}
            />

            <Column
              width={250}
              align="left"
              title={t(
                'scenes.data_ingester.upload_files_history.scene.table.columns.status'
              )}
              key="status"
              render={(row: DiIngestJobGridMode) => {
                const isLoading =
                  row.statusCode === 'initial_extraction' ||
                  row.statusCode === 'queued_for_extraction';

                return (
                  <div className="status-container">
                    <div
                      className={`status-dot ${isLoading ? 'loading' : ''}`}
                      style={{ backgroundColor: row.statusColor }}
                    />
                    <span>{row.statusTitle}</span>
                  </div>
                );
              }}
            />

            <Column
              width={120}
              align="center"
              title=""
              key="actions"
              render={(row) => (
                <Dropdown
                  menu={{ items: getDropdownMenu(row) }}
                  placement="bottomLeft"
                  trigger={['click']}
                >
                  <Button size="small">
                    <MoreOutlined />
                  </Button>
                </Dropdown>
              )}
            />
          </Table>
        </Card>

        {/* Modals */}
        <UploadFileModal
          isOpen={isNewResourceModalOpen}
          handleModalCancel={handleCloseNewResourceModal}
          submit={uploadFile}
          clients={diClients}
        />

        {selectedRow && (
          <ColumnMappingModal
            isOpen={isDetailModalOpen}
            handleModalCancel={handleCloseShowDetailResourceModal}
            jobId={selectedRow.id}
            fieldsFromDataBase={diTableColumns}
            parsingFormats={diParsingFormats}
            saveColumnMapping={saveColumnMapping}
          />
        )}

        {selectedRow && (
          <ErrorDetailModal
            isOpen={isErrorDetailModalOpen}
            handleModalCancel={handleCloseShowErrorDetailResourceModal}
            job={selectedRow}
          />
        )}
      </Layout>
    </>
  );
}

export default UploadFilesHistory;
